import React, { useState, useRef, useCallback, useEffect } from 'react';
import { CModal, CModalBody, CRow, CCol, CButton, CFormTextarea } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import {
    cilMap, cilCloudDownload, cilChevronLeft, cilChevronRight,
    cilThumbUp, cilThumbDown, cilCheckCircle, cilSave,
    cilLocationPin, cilZoomIn, cilZoomOut, cilX
} from '@coreui/icons';

import { calculatePriorityResult, calculateAssignedCompany, getPriorityTheme } from './utils';
import JsonDataRenderer from './JsonDataRenderer';
import { getCategoryByItem, getCategoryConfig } from '../CategoryConfig';

const UnifiedDetailModal = ({
    visible, onClose, item, handlePrev, handleNext,
    handleDownloadImage, handleOpenInGoogleMaps, handleFlyTo,
    latitudSenal, longitudSenal, senaleticaDetectada, lastViewedRowId,
    fechaDeteccionSenal, urlFrame, mapFilter,
    currentFeedbackStatus, setCurrentFeedbackStatus, manualObservations, setManualObservations,
    isSubmittingFeedback, isSavedObservations, showObservationInput, setShowObservationInput,
    handleQuickFeedback, handleSaveObservations, onSave
}) => {
    const [zoom, setZoom] = useState(1);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const imgContainerRef = useRef(null);
    const dragRef = useRef({ active: false, startX: 0, startY: 0, lastX: 0, lastY: 0, isDragging: false });

    const handleWheel = useCallback((e) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.15 : 0.15;
            setZoom(z => Math.max(0.5, Math.min(4, z + delta)));
        }
    }, []);

    useEffect(() => {
        setZoom(1);
        setTranslate({ x: 0, y: 0 });
        const d = dragRef.current;
        d.lastX = 0;
        d.lastY = 0;
    }, [item]);

    useEffect(() => {
        const el = imgContainerRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [handleWheel]);

    const onMouseDown = useCallback((e) => {
        // Only prevent default if we're not clicking on the buttons
        if (e.target.closest('button')) return;
        e.preventDefault();
        const d = dragRef.current;
        d.active = true;
        d.isDragging = false;
        d.startX = e.clientX - d.lastX;
        d.startY = e.clientY - d.lastY;
        
        // Save initial click position to detect dragging
        d.initialClickX = e.clientX;
        d.initialClickY = e.clientY;

        if (imgContainerRef.current) {
            imgContainerRef.current.style.cursor = 'grabbing';
        }
    }, []);

    const onMouseMove = useCallback((e) => {
        const d = dragRef.current;
        if (!d.active) return;
        
        // Detect if it's a drag or a click
        if (!d.isDragging) {
            const distance = Math.sqrt(Math.pow(e.clientX - d.initialClickX, 2) + Math.pow(e.clientY - d.initialClickY, 2));
            if (distance > 3) {
                d.isDragging = true;
            }
        }
        
        if (zoom <= 1) return; // Only pan when zoomed in
        
        const x = e.clientX - d.startX;
        const y = e.clientY - d.startY;
        d.lastX = x;
        d.lastY = y;
        setTranslate({ x, y });
    }, [zoom]);

    const onMouseUp = useCallback((e) => {
        const d = dragRef.current;
        if (!d.active) return;
        d.active = false;
        
        if (imgContainerRef.current) {
            imgContainerRef.current.style.cursor = zoom > 1 ? 'grab' : 'default';
        }

        // Toggling Zoom logic on single click
        if (!d.isDragging) {
            setZoom(prevZoom => {
                if (prevZoom > 1) {
                    setTranslate({ x: 0, y: 0 });
                    d.lastX = 0;
                    d.lastY = 0;
                    return 1;
                } else {
                    return 2.5; // Default zoom depth on click
                }
            });
        }
    }, [zoom]);

    useEffect(() => {
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    const resetZoom = useCallback(() => {
        setZoom(1);
        setTranslate({ x: 0, y: 0 });
    }, []);

    const zoomIn = useCallback(() => setZoom(z => Math.min(4, z + 0.25)), []);
    const zoomOut = useCallback(() => setZoom(z => Math.max(0.5, z - 0.25)), []);

    if (!item) return null;

    const priority = calculatePriorityResult(item);
    const company = calculateAssignedCompany(item);
    const priorityTheme = getPriorityTheme(priority);
    const categoryConfig = getCategoryByItem(item) || getCategoryConfig(mapFilter);
    const jsonFields = categoryConfig?.jsonFields || [];

    const isValidValue = (val) => {
        if (val === null || val === undefined || val === '') return false;
        const s = val.toString().trim().toUpperCase();
        return s !== 'N/A' && s !== 'NONE' && s !== 'NULL' && s !== '0';
    };

    return (
        <CModal alignment="center" scrollable visible={visible} onClose={onClose} size="xl" className="premium-modal-unified">
            <CModalBody className="p-0 d-flex flex-column" style={{ maxHeight: '90vh', minHeight: '500px' }}>
                <CRow className="g-0 flex-grow-1">
                    {/* ===== LEFT: IMAGE WITH ZOOM + DRAG ===== */}
                    <CCol lg={7} className="p-0 position-relative bg-dark d-flex flex-column" style={{ minHeight: '400px' }}>
                        <div ref={imgContainerRef}
                            className="flex-grow-1 d-flex align-items-center justify-content-center position-relative overflow-hidden"
                            style={{
                                backgroundColor: '#0a0a0a',
                                cursor: zoom > 1 ? 'grab' : 'default',
                                userSelect: 'none'
                            }}
                            onMouseDown={onMouseDown}>
                            <img src={urlFrame} alt="Frame"
                                className="img-fluid"
                                draggable={false}
                                style={{
                                    objectFit: 'contain',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    transform: `translate(${translate.x}px, ${translate.y}px) scale(${zoom})`,
                                    transition: dragRef.current.active ? 'none' : 'transform 0.15s ease',
                                    pointerEvents: 'none'
                                }}
                                loading="lazy" />

                            {/* Prev / Next */}
                            <div className="position-absolute top-50 start-0 translate-middle-y ps-2 z-2">
                                <CButton color="light" variant="ghost" className="rounded-circle p-0 shadow d-flex align-items-center justify-content-center"
                                    style={{ width: '36px', height: '36px', backgroundColor: 'rgba(255,255,255,0.2)', border: 'none' }} onClick={handlePrev}>
                                    <CIcon icon={cilChevronLeft} size="lg" className="text-white" />
                                </CButton>
                            </div>
                            <div className="position-absolute top-50 end-0 translate-middle-y pe-2 z-2">
                                <CButton color="light" variant="ghost" className="rounded-circle p-0 shadow d-flex align-items-center justify-content-center"
                                    style={{ width: '36px', height: '36px', backgroundColor: 'rgba(255,255,255,0.2)', border: 'none' }} onClick={handleNext}>
                                    <CIcon icon={cilChevronRight} size="lg" className="text-white" />
                                </CButton>
                            </div>

                            {/* Metadata overlay */}
                            <div className="position-absolute bottom-0 start-0 p-3 z-2 d-flex gap-2 align-items-center flex-wrap"
                                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)', width: '100%', paddingBottom: '20px' }}>
                                <span className="badge px-3 py-2 shadow-sm" style={{ fontSize: '0.75rem', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px' }}>ID #{lastViewedRowId}</span>
                                <span className="badge px-3 py-2 shadow-sm" style={{ fontSize: '0.75rem', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px' }}>{senaleticaDetectada || item?.tipo_especifico}</span>
                                {categoryConfig?.hasPriority && (
                                    <span className="badge px-3 py-2 shadow-sm" style={{ fontSize: '0.75rem', backgroundColor: priorityTheme.color, color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', boxShadow: `0 0 10px ${priorityTheme.color}88` }}>{priority}</span>
                                )}
                            </div>

                            {/* Zoom controls */}
                            <div className="position-absolute bottom-0 end-0 p-3 z-3 d-flex gap-1" style={{ marginBottom: '5px' }}>
                                <div className="d-flex align-items-center shadow-lg rounded-pill p-1 border" style={{ borderColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                    <CButton size="sm" className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                        style={{ width: '32px', height: '32px', backgroundColor: 'transparent', border: 'none' }}
                                        onClick={resetZoom} title="Reset Zoom">
                                        <span style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>1:1</span>
                                    </CButton>
                                    <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }}></div>
                                    <CButton size="sm" className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                        style={{ width: '32px', height: '32px', backgroundColor: 'transparent', border: 'none' }}
                                        onClick={zoomOut}>
                                        <CIcon icon={cilZoomOut} size="sm" />
                                    </CButton>
                                    <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }}></div>
                                    <CButton size="sm" className="rounded-circle d-flex align-items-center justify-content-center text-white"
                                        style={{ width: '32px', height: '32px', backgroundColor: 'transparent', border: 'none' }}
                                        onClick={zoomIn}>
                                        <CIcon icon={cilZoomIn} size="sm" />
                                    </CButton>
                                    <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.2)' }}></div>
                                    <span className="d-flex align-items-center px-2 text-white fw-bold"
                                        style={{ fontSize: '0.7rem', minWidth: '45px', justifyContent: 'center' }}>
                                        {Math.round(zoom * 100)}%
                                    </span>
                                </div>
                            </div>
                        </div>
                    </CCol>

                    {/* ===== RIGHT: SIDEBAR (modern bento aesthetic) ===== */}
                    <CCol lg={5} className="p-0 d-flex flex-column sidebar-info-container"
                        style={{ 
                            maxHeight: '90vh', 
                            overflowY: 'auto', 
                            backgroundColor: '#f8f9fa',
                            borderLeft: '1px solid rgba(0,0,0,0.05)'
                        }}>
                        {/* HEADER */}
                        <div className="d-flex justify-content-between align-items-center px-4 py-3 sticky-top z-1" 
                            style={{ 
                                background: 'rgba(255,255,255,0.85)', 
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                borderBottom: '1px solid rgba(0,0,0,0.05)'
                            }}>
                            <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                <div className="d-flex gap-1 bg-light rounded-pill p-1 border shadow-sm">
                                    <CButton color="light" size="sm" className="rounded-circle d-flex align-items-center justify-content-center border-0"
                                        style={{ width: '28px', height: '28px', padding: 0, fontSize: '0.85rem', background: 'transparent' }}
                                        onClick={handlePrev} title="Anterior"><CIcon icon={cilChevronLeft} size="sm"/></CButton>
                                    <CButton color="light" size="sm" className="rounded-circle d-flex align-items-center justify-content-center border-0"
                                        style={{ width: '28px', height: '28px', padding: 0, fontSize: '0.85rem', background: 'transparent' }}
                                        onClick={handleNext} title="Siguiente"><CIcon icon={cilChevronRight} size="sm"/></CButton>
                                </div>
                                <h5 className="fw-bold mb-0 text-truncate" style={{ color: '#131133', fontSize: '1.05rem', letterSpacing: '-0.3px' }}>
                                    Detección #{lastViewedRowId}
                                </h5>
                            </div>
                            <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                <div className="d-flex align-items-center bg-white rounded-pill p-1 border shadow-sm gap-1">
                                    <CButton size="sm" className="btn-feedback-quick"
                                        style={{
                                            border: 'none', background: 'none', padding: '6px', borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all 0.2s ease', cursor: 'pointer',
                                            color: currentFeedbackStatus === 1 ? '#2eb85c' : '#aeb2b8',
                                            backgroundColor: currentFeedbackStatus === 1 ? 'rgba(46,184,92,0.15)' : 'transparent',
                                            transform: currentFeedbackStatus === 1 ? 'scale(1.05)' : 'none'
                                        }}
                                        onClick={() => handleQuickFeedback('bien')} disabled={isSubmittingFeedback} title="Correcto">
                                        <CIcon icon={cilThumbUp} size="sm" />
                                    </CButton>
                                    <div style={{ width: '1px', height: '16px', background: '#e0e0e0' }}></div>
                                    <CButton size="sm" className="btn-feedback-quick"
                                        style={{
                                            border: 'none', background: 'none', padding: '6px', borderRadius: '50%',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all 0.2s ease', cursor: 'pointer',
                                            color: currentFeedbackStatus === 3 ? '#e55353' : '#aeb2b8',
                                            backgroundColor: currentFeedbackStatus === 3 ? 'rgba(229,83,83,0.15)' : 'transparent',
                                            transform: currentFeedbackStatus === 3 ? 'scale(1.05)' : 'none'
                                        }}
                                        onClick={() => handleQuickFeedback('mal')} disabled={isSubmittingFeedback} title="Incorrecto">
                                        <CIcon icon={cilThumbDown} size="sm" />
                                    </CButton>
                                </div>
                                <CButton color="light" size="sm" className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                                    style={{ width: '32px', height: '32px', border: '1px solid #dee2e6', padding: 0, background: '#fff' }}
                                    onClick={onClose} title="Cerrar"><CIcon icon={cilX} size="sm"/></CButton>
                            </div>
                        </div>

                        {/* SCROLLABLE CONTENT */}
                        <div className="px-4 py-3 flex-grow-1 d-flex flex-column gap-3" style={{ overflowY: 'auto' }}>
                            
                            {/* Observation input bubble */}
                            {showObservationInput && (
                                <div className="p-3 shadow-sm mb-1" style={{ background: '#131133', borderRadius: '16px', position: 'relative' }}>
                                    <div style={{ position: 'absolute', top: '-6px', right: '90px', width: '12px', height: '12px', background: '#131133', transform: 'rotate(45deg)' }}></div>
                                    <div className="d-flex align-items-center gap-3">
                                        <CFormTextarea placeholder="Escribir nota de auditoría..." rows={1} value={manualObservations}
                                            onChange={(e) => setManualObservations(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSaveObservations(); } }}
                                            className="bg-transparent border-0 shadow-none text-white p-0"
                                            style={{ fontSize: '0.9rem', resize: 'none', minHeight: '26px' }} autoFocus />
                                        <CButton size="sm" className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                            style={{
                                                width: '32px', height: '32px', border: 'none',
                                                backgroundColor: isSavedObservations ? '#2eb85c' : 'rgba(255,255,255,0.15)',
                                                color: '#fff', transition: 'all 0.3s ease'
                                            }}
                                            onClick={handleSaveObservations} disabled={isSubmittingFeedback}>
                                            <CIcon icon={isSavedObservations ? cilCheckCircle : cilSave} size="sm" />
                                        </CButton>
                                    </div>
                                </div>
                            )}

                            {/* ID + Date pill (Colorful minimal style) */}
                            <div className="d-flex justify-content-between align-items-center px-3 py-2 bg-white rounded-pill border shadow-sm mb-1" style={{ fontSize: '0.75rem' }}>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted fw-bold">Detección #{lastViewedRowId}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-dark fw-bold">{fechaDeteccionSenal}</span>
                                </div>
                            </div>

                            {/* Priority & Company Info */}
                            <div className="d-flex gap-2">
                                {categoryConfig?.hasCompany && (
                                    <div className="flex-grow-1 px-3 py-2 bg-white border shadow-sm position-relative overflow-hidden" style={{ borderRadius: '0 12px 12px 0', borderLeft: '4px solid #5856D6' }}>
                                        <span className="text-uppercase text-muted fw-bold d-block mb-1" style={{ fontSize: '0.55rem', letterSpacing: '0.5px' }}>Empresa Asignada</span>
                                        <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>{company}</span>
                                    </div>
                                )}
                                {categoryConfig?.hasPriority && (
                                    <div className="flex-grow-1 px-3 py-2 shadow-sm position-relative overflow-hidden"
                                        style={{ 
                                            borderRadius: '0 12px 12px 0', 
                                            backgroundColor: priorityTheme.bg,
                                            borderTop: `1px solid ${priorityTheme.color}33`,
                                            borderRight: `1px solid ${priorityTheme.color}33`,
                                            borderBottom: `1px solid ${priorityTheme.color}33`,
                                            borderLeft: `4px solid ${priorityTheme.color}`
                                        }}>
                                        <span className="text-uppercase fw-bold d-block mb-1" style={{ fontSize: '0.55rem', letterSpacing: '0.5px', color: priorityTheme.color }}>Prioridad</span>
                                        <span className="fw-bold" style={{ fontSize: '0.9rem', color: priorityTheme.color }}>{priority}</span>
                                    </div>
                                )}
                            </div>

                            {/* Address Box */}
                            <div>
                                <span className="text-uppercase text-muted fw-bold d-block mb-1" style={{ fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                                    Dirección / Referencia
                                </span>
                                <div className="address-box shadow-sm" style={{ borderRadius: '0 12px 12px 0', backgroundColor: '#fff', borderLeft: '4px solid #3d99eb' }}>
                                    <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
                                        <span className="fw-semibold text-dark small text-truncate" title={item?.direccion} style={{ flex: 1, minWidth: 0 }}>
                                            {item?.direccion || 'Sin dirección registrada'}
                                        </span>
                                        <CButton color="success" size="sm" className="text-white d-flex align-items-center shadow-sm flex-shrink-0"
                                            style={{ borderRadius: '8px', fontSize: '0.65rem', padding: '3px 10px' }}
                                            onClick={() => handleOpenInGoogleMaps(latitudSenal, longitudSenal)}>
                                            <CIcon icon={cilMap} size="sm" className="me-1" /> Maps
                                        </CButton>
                                    </div>
                                    <div className="font-monospace text-muted" style={{ fontSize: '0.65rem' }}>
                                        {latitudSenal}, {longitudSenal}
                                    </div>
                                </div>
                            </div>

                            {/* Section divider */}
                            <div className="border-bottom my-1"></div>

                            {/* Technical Details Box */}
                            {jsonFields.length > 0 && item?.json_data !== null && (
                                <div className="additional-info-box shadow-sm p-3" style={{ borderRadius: '0 12px 12px 0', border: '1px solid #feebc8', borderLeft: '4px solid #f6ad55' }}>
                                    <h6 className="fw-bold border-bottom pb-2 mb-3 text-dark" style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}>
                                        📋 {categoryConfig?.label?.toUpperCase() || 'DETALLES TÉCNICOS'}
                                    </h6>
                                    <JsonDataRenderer jsonData={item?.json_data || {}} fields={jsonFields} />
                                </div>
                            )}
                            {jsonFields.length > 0 && item?.json_data === null && (
                                <div className="additional-info-box shadow-sm p-3" style={{ borderRadius: '0 12px 12px 0', borderLeft: '4px solid #a0aec0', backgroundColor: '#f7fafc' }}>
                                    <h6 className="fw-bold border-bottom pb-2 mb-3 text-dark" style={{ fontSize: '0.8rem', letterSpacing: '0.5px' }}>
                                        🕐 {categoryConfig?.label?.toUpperCase() || 'DETALLES TÉCNICOS'}
                                    </h6>
                                    <div className="d-flex align-items-center gap-2 py-1">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                        <span className="text-muted small">
                                            Clasificación en proceso...
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* IA Observations */}
                            {(isValidValue(item?.descripcion) || isValidValue(item?.json_data?.observaciones)) && (
                                <div>
                                    <span className="text-uppercase text-muted fw-bold d-block mb-1" style={{ fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                                        Comentarios de la IA
                                    </span>
                                    <div className="address-box shadow-sm" style={{ borderLeft: '4px solid #805ad5', borderRadius: '0 12px 12px 0', backgroundColor: '#faf5ff' }}>
                                        <div className="text-dark small" style={{ lineHeight: '1.4' }}>
                                            {item?.descripcion || item?.json_data?.observaciones}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* STICKY BOTTOM */}
                        <div className="p-3 border-top sticky-bottom z-1 d-flex gap-3" 
                            style={{ 
                                background: 'rgba(255,255,255,0.85)', 
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)'
                            }}>
                            <CButton size="sm" className="flex-grow-1 d-flex align-items-center justify-content-center gap-2 text-white shadow-sm fw-semibold"
                                style={{ borderRadius: '12px', padding: '10px', background: 'linear-gradient(135deg, #131133 0%, #2a2566 100%)', border: 'none' }}
                                onClick={() => handleFlyTo(latitudSenal, longitudSenal)}>
                                <CIcon icon={cilLocationPin} size="sm" /> Ver en el Mapa
                            </CButton>
                            <CButton color="light" size="sm" className="d-flex align-items-center justify-content-center shadow-sm border"
                                style={{ borderRadius: '12px', width: '48px', padding: '10px' }}
                                onClick={() => handleDownloadImage(urlFrame)} title="Descargar">
                                <CIcon icon={cilCloudDownload} size="lg" className="text-dark" />
                            </CButton>
                        </div>
                    </CCol>
                </CRow>
            </CModalBody>
        </CModal>
    );
};

export default React.memo(UnifiedDetailModal);
